import { Alert, Button, Col, Form, Popover, Row, Space } from "antd";
import { Link, useNavigate } from "react-router-dom";
import FormItem, { createRegexRule, FormItemPropsType, REGEX_EMAIL } from "../../form/form-item";
import { useCallback, useEffect, useMemo, useState } from "react";

import PlamoApiWithSaveNotification from "../../util/with-save-notification";
import { AdvancedForm } from "../../form/advanced-form";
import { useGetAssignmentsQuery, useGetCustomersQuery, useGetEmployeeQuery, useGetEmployeesQuery, useGetInsurancesQuery, useGetLocationsQuery } from "../../../../api/plamo-api";
import dayjs from "dayjs";
import { IconCopySolidFull, IconLinkSolidFull } from "../../../../icon";
import ConfirmPasswordButton from "../../util/confirm-password-button";
const { useCreateCustomerWithSaveNotificationMutation, useUpdateCustomerWithSaveNotificationMutation, useDeleteCustomerWithSaveNotificationMutation } = PlamoApiWithSaveNotification;

function haversineDistance(lat1, lon1, lat2, lon2) {
    const R = 6371; // Earth radius in kilometers

    const toRadians = deg => deg * (Math.PI / 180);

    const dLat = toRadians(lat2 - lat1);
    const dLon = toRadians(lon2 - lon1);

    const a = Math.sin(dLat / 2) ** 2 +
              Math.cos(toRadians(lat1)) * Math.cos(toRadians(lat2)) *
              Math.sin(dLon / 2) ** 2;

    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

    return R * c; // distance in kilometers
}

const useGetActiveAndNotExitingAndWithCapacityEmployeesQuery = (queryArgs, ...rest) => {
  const modifiedQueryArgs = useMemo(() => {
    let filterStr = queryArgs?.f || '{}';
    const filter = JSON.parse(filterStr) as any;
    filter.active = { c: "EQ", v: true };
    filter.dateOfExit = { c: "EQ", v: null };
    filter.capacity = { c: "EQ", v: 'y' };
    filterStr = JSON.stringify(filter);
    return {...(queryArgs) || {}, f: filterStr}
  }, [queryArgs]);
  return useGetEmployeesQuery(modifiedQueryArgs, ...rest);
}

const AvailableEmployees = ({ fieldName, label, field, form, customerId }: FormItemPropsType & { form: any, customerId: number }) => {
  const { data: employees } = useGetActiveAndNotExitingAndWithCapacityEmployeesQuery({}) as any;
  const navigate = useNavigate();
  const [nearestEmployees, setNearestEmployees] = useState<any>([]);
  const addressStreet = Form.useWatch("addressStreet", form);
  const addressHouseNumber = Form.useWatch("addressHouseNumber", form);
  const addressZipCode = Form.useWatch("addressZipCode", form);
  const addressCity = Form.useWatch("addressCity", form);
  const combinedFieldName = useMemo(() => [...(field ? [field.name] : []), fieldName], [field, fieldName])
  const spans = {
    sm: 40,
    md: 40,
    lg: 20,
    xl: 16,
    xxl: 10
  }

  console.log("Employees", employees)

  useEffect(() => {
    (async () => {
      try {
        if(addressZipCode?.match(/^\d{5}$/) && employees?.items) {
          let search = ' ';
          if(addressStreet?.length) {
            search += addressStreet;
          }
          if(addressHouseNumber?.length) {
            search += " " + addressHouseNumber
          }
          search += " " + addressZipCode
          if(addressCity?.length) {
            search += " " + addressCity
          }

          const response = await fetch(`https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(search)}&format=json`, { redirect: 'follow' });
          const result = await response.json();

          if(result.length > 0) {
            const lat = parseFloat(result[0].lat);
            const lon = parseFloat(result[0].lon);

            const employeesWithDistance: any[] | undefined = employees?.items.map(e => ({...e, dist: haversineDistance(e.lat, e.lon, lat, lon) })).sort((a, b) => a.dist - b.dist)

            setNearestEmployees(employeesWithDistance?.slice(0, 5))

            console.log("Found lat / lon", lat, lon, result, employeesWithDistance)
          }
        }
      } catch(e) {
        console.error("Could not find nearest employees", e);
      }
    })()
  }, [addressStreet, addressHouseNumber, addressZipCode, addressCity, employees?.items, form])
  
  return (
    <Col {...spans}>
      <Form.Item 
        {...field}
        labelCol={{ span: 10 }}
        wrapperCol={{ span: 14 }}
        name={combinedFieldName}
        label={label}
      >
        <ul style={{ margin: 0 }}>
          { nearestEmployees.map(e => <li><Link to={`/employee/${e.id}`}>{ e.firstName } { e.lastName }</Link> ({e.dist.toFixed(1) }km) { customerId && <Button type="link" icon={<IconLinkSolidFull />} onClick={() => navigate(`/assignment/new?preselectedCustomerId=5&preselectedEmployeeId=${e.id}`)} /> }</li>)}
        </ul>
      </Form.Item>
    </Col>
  );
}

function CustomerMainForm({ customerId, customer }) {
  const [createCustomer, { isLoading: isCreatingCustomer }] = useCreateCustomerWithSaveNotificationMutation()
  const [updateCustomer, { isLoading: isUpdatingCustomer }] = useUpdateCustomerWithSaveNotificationMutation()
  const [deleteCustomer] = useDeleteCustomerWithSaveNotificationMutation();
  const { data: assignments } = useGetAssignmentsQuery({ f: JSON.stringify({ "customer.id": { c: "EQ", v: customerId } }), st: JSON.stringify({ id: "desc" }) }) as any;
  const currentAssignment = useMemo(() => assignments?.items?.filter(a => (!a.startDate || dayjs(a.startDate).valueOf() <= dayjs().valueOf()) && (!a.endDate || dayjs(a.endDate).add(1, 'day').valueOf() >= dayjs().valueOf()))[0], [assignments]);
  const { data: assignedEmployee } = useGetEmployeeQuery({ id: currentAssignment?.employeeId }, { skip: !currentAssignment }) as any;
  const [form] = Form.useForm();
  const cancellationDate = Form.useWatch("cancellationDate", form);
  const cancellationBy = Form.useWatch("cancellationBy", form);
  const customerFirstName = Form.useWatch("firstName", form);
  const customerLastName = Form.useWatch("lastName", form);
  const customerDateOfBirth = Form.useWatch("dateOfBirth", form);
  const dailyCareChecked = Form.useWatch("dailyCare", form);
  const supportAmountChecked = Form.useWatch("supportAmount", form);
  const absenceCareChecked = Form.useWatch("absenceCare", form)?.includes(`Genehmigt ${dayjs().year()}`) ;
  const shortTermCareChecked = Form.useWatch("shortTermCare", form)?.includes(`Genehmigt ${dayjs().year()}`) ;
  const conversionChecked = Form.useWatch("conversion", form)?.includes('Genehmigt');
  const selfFundedChecked = Form.useWatch("selfFunded", form);
  const careGrade = Form.useWatch("careGrade", form);
  const counselingChecked = Form.useWatch("counseling", form);
  const addressZipCode: string = Form.useWatch("addressZipCode", form);
  const supportAmountSince = Form.useWatch("supportAmountSince", form);
  const subsidy = Form.useWatch("subsidy", form);
  const supportAmountSinceStr = useMemo(() => dayjs(supportAmountSince).format('YYYY-MM-DD'), [supportAmountSince])
  const supportAmountInitialAmount = Form.useWatch("supportAmountInitialAmount", form);
  const [copyMainDataPopupOpen, setCopyMainDataPopupOpen] = useState<boolean>(false);
  //const { data: counselings } = useGetCounselingsQuery({ f: JSON.stringify({ "customer.id": { c: "EQ", v: customerId }}), st: JSON.stringify({ id: 'asc' }) }) as any

  const { data: existingCustomers } = useGetCustomersQuery({ f: JSON.stringify({ firstName: { c: "EQ", v: customerFirstName?.trim() }, lastName: { c: "EQ", v: customerLastName?.trim() } })}, { skip: customerId || !customerFirstName || !customerLastName }) as any;
  const existingCustomer = existingCustomers?.items?.filter(c => dayjs(c.dateOfBirth).format('DD.MM.YYYY') === dayjs(customerDateOfBirth).format('DD.MM.YYYY') )[0];
  
  const navigate = useNavigate();
 
  const isLoading = isCreatingCustomer || isUpdatingCustomer;
  
  useEffect(() => {
    if(cancellationBy) {
      const values = form.getFieldsValue();
      form.setFieldsValue({...values, status: 'Inaktiv' });
    }
  }, [cancellationBy, form])

  /*
  useEffect(() => {
    (async () => {
      try {
        if(addressZipCode?.match(/^\d{5}$/)) {
          let values = form.getFieldsValue();
          form.setFieldsValue({...values, addressCity: null, addressArea: null});

          const response = await fetch(`https://nominatim.openstreetmap.org/search?postalcode=${addressZipCode}&country=Deutschland&format=json&addressdetails=1&&accept-language=de`)
          const data = await response.json();

          const addressCity = data[0]?.address?.city || data[0]?.address?.village;
          const addressArea = data[0]?.address?.city_district || data[0]?.address?.suburb

          values = form.getFieldsValue();
          if(!values.addressCity?.length) {
            values = {...values, addressCity }
          }
          if(!values.addressArea?.length) {
            values = {...values, addressArea }
          }
          form.setFieldsValue(values);
        }
      } catch(e) {
        console.error("Could not fetch city for zip code", e);
      }
    })()
  }, [addressZipCode, form])
  */

  useEffect(() => {
    const startDate = dayjs(supportAmountSinceStr).date(1).hour(0).minute(0).second(0).millisecond(0); // your date
    const today = dayjs().date(1).hour(0).minute(0).second(0).millisecond(0);

    const months = today.diff(startDate, 'month') + 1;

    const values = form.getFieldsValue();
    form.setFieldsValue({...values, supportAmountBudget: (supportAmountInitialAmount || 0) + months * 131 });
  }, [supportAmountSinceStr, supportAmountInitialAmount, form])

  /*
  useEffect(() => {
    form.setFieldsValue(customer)
  }, [form, customer])
  */
  
  const onFinish = useCallback(async (values: any) => {
    if(!customerId) {
      const { data: newCustomer }:any = await createCustomer(values);
      console.log("Created new customer", newCustomer);
      navigate(`./../${newCustomer.id}/mainForm`)
    } else {
      const newCustomer = {...customer, ...values}
      await updateCustomer(newCustomer);
    }
  }, [customerId, createCustomer, updateCustomer, navigate, customer]);

  const originalValues = useMemo(() => customerId ? customer : { status: "Neukunde", followUp: dayjs().add(1, 'week'), entryDate: dayjs() }, [customerId, customer])

  /*
  const lastCounseling = useMemo(() => {
    return counselings?.items?.filter(c => c.tookPlace).sort((a, b) => dayjs(b.date).valueOf() - dayjs(a.date).valueOf())[0]
  }, [counselings?.items])

  const nextCounseling = useMemo(() => {
    const today00 = dayjs().hour(0).minute(0)
    console.log("Counselings sorted", counselings?.items?.sort((a, b) => dayjs(a.date).valueOf() - dayjs(b.date).valueOf()))
    return null; //return counselings?.items?.sort((a, b) => dayjs(a.date).valueOf() - dayjs(b.date).valueOf())[0]
  }, [counselings?.items])
  */

  const onDelete = useCallback(async () => {
    await deleteCustomer({ id: customerId });
    navigate('/customer');
  }, [customerId, deleteCustomer, navigate])

  const copyMainData = useCallback(() => {
    const values = form.getFieldsValue();
    const data = `${values.firstName || ''} ${values.lastName || ''}\n`
      + `${values.addressStreet || ''} ${values.addressHouseNumber || ''}\n`
      + `${values.addressZipCode || ''} ${values.addressCity || ''}\n`
      + `${values.phone || ''}\n`
      + `${values.mobile || ''}\n`

    navigator.clipboard.writeText(data);
    setCopyMainDataPopupOpen(true);
    setTimeout(() => setCopyMainDataPopupOpen(false), 2000);
  }, [form, setCopyMainDataPopupOpen])

  const approvalOptions = ['Gewünscht', ...(new Array(dayjs().year() - 2023).fill(0).map((_, i) => `Genehmigt ${2025 + i}`))].map(v => ({ label: v, value: v }));

  return (
    <AdvancedForm form={form} name="customer" onFinish={onFinish} isLoading={isLoading} originalValues={originalValues} initialEditing={!customerId}>
      { !customerId && existingCustomer && <Row><Alert style={{ width: '100%', marginBottom: '1em', marginTop: '1em' }} message={<>Es ist bereits ein <Link to={`/customer/${existingCustomer.id}`}>Kunde</Link> mit selbem Namen und Geburtsdatum angelegt.</>} type="warning" /></Row> }
      <Row><h3>Betreuung</h3></Row>
      <Row gutter={24}>
        <FormItem fieldName="xxx" label="Betreut durch" type="staticLink" typeData={{ value: assignedEmployee ? `${assignedEmployee.firstName} ${assignedEmployee.lastName}` : '', onClick: () => !customerId ? () => {} : (assignedEmployee ? navigate(`/employee/${assignedEmployee.id}`) : navigate(`/customer/${customerId}/assignments` )) }} />
          { !assignedEmployee && addressZipCode?.match(/\d{5}/) && <AvailableEmployees fieldName="xxx" label="Verfügb. Mitarb." form={form} customerId={customerId} /> }
      </Row>
      <Row><h3>Persönliche Daten <Popover
          content={<>Daten kopiert</>}
          open={copyMainDataPopupOpen}
        ><Button icon={<IconCopySolidFull />} type="link" onClick={copyMainData}/></Popover></h3></Row>
      <Row gutter={24}>
        <FormItem fieldName="salutation" label="Anrede" type="backendEnum" typeData={{ ident: "salutation" }}/>
        <FormItem fieldName="firstName" label="Vorname" />
        <FormItem fieldName="lastName" label="Nachname" />
        <FormItem fieldName="dateOfBirth" label="Geburtsdatum" type="date" />
        <FormItem fieldName="id" label="Kundennummer" type="number" readOnly />
      </Row>
      <Row gutter={24}>
        <FormItem fieldName="addressStreet" label="Straße" />
        <FormItem fieldName="addressHouseNumber" label="Hausnummer" />
        <FormItem fieldName="addressZipCode" label="PLZ" rules={[createRegexRule(/^\d{5}$/, 'Keine gültige PLZ')]} />
        <FormItem fieldName="addressCity" label="Ort" />
        <FormItem fieldName="addressArea" label="Stadtteil" />
      </Row>
      <Row gutter={24}>
        <FormItem fieldName="phone" label="Festnetz" />
        <FormItem fieldName="mobile" label="Handy" />
        <FormItem fieldName="email" label="E-Mail" rules={[createRegexRule(REGEX_EMAIL, 'Keine gültige E-Mail-Adresse')]} />
        <FormItem fieldName="locationId" label="Standort" type="ref" typeData={{ query: useGetLocationsQuery, getId: location => location.id, render: location => location.name }} />
      </Row>
      <Row><h3>Versicherung</h3></Row>
      <Row gutter={24}>
        { false && <FormItem fieldName="insurance" label="Krankenkasse" type="backendEnum" typeData={{ showSearch: true, ident: "insurance" }} /> }
        <FormItem fieldName="insuranceId" label="Krankenkasse" type="ref" typeData={{ query: useGetInsurancesQuery, getId: insurance => insurance.id, render: insurance => insurance.name }} />
        <FormItem fieldName="socialSecurityNumber" label="Versichertennummer" />
        <FormItem fieldName="subsidy" label="Beihilfe" type="check" />
        { subsidy && <FormItem fieldName="subsidyShare" label="Beihilfeanteil" type="number" typeData={{ min: 0, max: 100 }} /> }
      </Row>
      <Row><h3>Leistung</h3></Row>
      <Row gutter={24}>
        <FormItem fieldName="dailyCare" label="Alltagshilfe" type="check" />
        <FormItem fieldName="domesticAid" label="Haushaltshilfe" type="check" />
        <FormItem fieldName="counseling" label="Beratungsbesuche" type="check" />
      </Row>
      { dailyCareChecked && 
      <>
        <Row gutter={24}>
          <FormItem fieldName="careGrade" label="Pflegegrad" type="backendEnum" typeData={{ ident: "careGrade" }} />
          { false && <FormItem fieldName="funder" label="Kostenträger" type="backendEnum" typeData={{ ident: "funder" }} /> }
          { false && <FormItem fieldName="frequency" label="Häufigkeit" type="enum" typeData={{ options: [{ label: "wöchentlich", value: "wöchentlich" }, { label: "alle 14 Tage", value: "alle 14 Tage" }]}} /> }
        </Row>
        { false && 
        <Row gutter={24}>
          <FormItem fieldName="startDate" label="Seit" type="date" />
          <FormItem fieldName="elb" label="ELB genutzt" type="check" />
          <FormItem fieldName="vp" label="VP genutzt" type="check" />
        </Row> }
        <Row gutter={24}>
          <FormItem fieldName="supportAmount" label="Entlastungsbetrag" type="check" />
          { supportAmountChecked && <><FormItem fieldName="supportAmountSince" label="ab" type="date" />
          <FormItem fieldName="supportAmountInitialAmount" label="Anfangsbetrag" type="currency" />
          <FormItem fieldName="supportAmountBudget" label="Budget" type="currency" />
          <FormItem fieldName="supportAmountPriority" label="Priorität" type="enum" typeData={{ options: [{ label: "1", value: "1" }, { label: "2", value: "2" }, { label: "3", value: "3" }, { label: "4", value: "4" }]}} /> </>}
        </Row>
        { ["2", "3", "4", "5"].includes(careGrade) && 
        <>
          <Row gutter={24}>
            <FormItem fieldName="absenceCare" label="Verhinderungspflege" type="enum" typeData={{ options: approvalOptions, multiple: true }} />
            { absenceCareChecked && <><FormItem fieldName="absenceCareSince" label="ab" type="date" />
            <FormItem fieldName="absenceCareBudget" label="Budget" type="currency" />
            <FormItem fieldName="absenceCarePriority" label="Priorität" type="enum" typeData={{ options: [{ label: "1", value: "1" }, { label: "2", value: "2" }, { label: "3", value: "3" }, { label: "4", value: "4" }]}} /></>}
          </Row>
          <Row gutter={24}>
            <FormItem fieldName="shortTermCare" label="Kurzzeitpflege" type="enum" typeData={{ options: approvalOptions, multiple: true }} />
            { shortTermCareChecked && <><FormItem fieldName="shortTermCareSince" label="ab" type="date" />
            <FormItem fieldName="shortTermCareBudget" label="Budget" type="currency" />
            <FormItem fieldName="shortTermCarePriority" label="Priorität" type="enum" typeData={{ options: [{ label: "1", value: "1" }, { label: "2", value: "2" }, { label: "3", value: "3" }, { label: "4", value: "4" }]}} /></>}
          </Row>
          <Row gutter={24}>
            <FormItem fieldName="conversion" label="Umwandlung" type="backendEnum" typeData={{ ident: "conversionStatus", multiple: true }} />
            { conversionChecked && <><FormItem fieldName="conversionSince" label="ab" type="date" />
            <FormItem fieldName="conversionBudget" label="Budget" type="currency" />
            <FormItem fieldName="conversionPriority" label="Priorität" type="enum" typeData={{ options: [{ label: "1", value: "1" }, { label: "2", value: "2" }, { label: "3", value: "3" }, { label: "4", value: "4" }]}} /></>}
          </Row>
        </>}
        <Row gutter={24}>
          <FormItem fieldName="selfFunded" label="Sonst. Budget" type="check" />
          { selfFundedChecked && <><FormItem fieldName="" label="" type="empty" />
          <FormItem fieldName="selfFundedBudget" label="Budget" type="number" typeData={{ addonAfter: "Std" }} />
          <FormItem fieldName="selfFundedPriority" label="Priorität" type="enum" typeData={{ options: [{ label: "1", value: "1" }, { label: "2", value: "2" }, { label: "3", value: "3" }, { label: "4", value: "4" }]}} /></>}
        </Row>
        { false && 
        <Row gutter={24}>
          <FormItem fieldName="unlimitedBudget" label="Unbegr. Budget" type="check" />
        </Row> }
      </>}
      {
        false && counselingChecked && <>
          <Row><h3>Beratungsbesuche</h3></Row>
          <Row gutter={24}>
            <FormItem fieldName="xxx" label="letzter Termin" type="staticLink" typeData={{ value: '', onClick: () => navigate(`/customer/${customerId}/counseling`) }} />
            <FormItem fieldName="xxx" label="nächster Termin" type="staticLink" typeData={{ value: '', onClick: () => navigate(`/customer/${customerId}/counseling`) }} />
            <FormItem fieldName="xxx" label="Beratungsperson" type="staticLink" typeData={{ value: '', onClick: () => navigate(`/customer/${customerId}/counseling`) }}  />
          </Row>
        </>
      }
      <Row><h3>Sonstiges</h3></Row>
      <Row gutter={24}>
        <FormItem fieldName="pets" label="Haustiere" type="backendEnum" typeData={{ ident: "pet" }}  />
        <FormItem fieldName="smoker" label="Raucher" type="enum" typeData={{ options: [{ value: "y", label: "Ja" }, { value: "n", label: "Nein" }] }}  />
      </Row>
      <Row gutter={24}>
        <FormItem fieldName="contactPoint" label="Aufmerksam durch" type="backendEnum" typeData={{ ident: "contactPoint" }} />
      </Row>
      <Row gutter={24}>
        <FormItem fieldName="status" label="Status" type="backendEnum" typeData={{ ident: "customerStatus" }} />
        <FormItem fieldName="entryDate" label="Erstellt" type="date" />
        <FormItem fieldName="followUp" label="Wiedervorlage" type="date" />
      </Row>
      <Row gutter={24}>
        <FormItem fieldName="anonymousEmployee" label="Anonym. Mitarb." type="check" />
      </Row>
      <Row gutter={24}>
        <FormItem fieldName="cancellationDate" label="Kündigungsdatum" type="date" />
        { cancellationDate && <FormItem fieldName="cancellationBy" label="Kündigung durch" type="backendEnum" typeData={{ ident: "cancellationBy" }} /> }
        { cancellationBy === 'Kunde' && <FormItem fieldName="cancellationReason" label="Kündigungsgrund" type="backendEnum" typeData={{ ident: "cancellationReasonCustomer" }} /> }
        { cancellationBy === 'Dienstleister' && <FormItem fieldName="cancellationReason" label="Kündigungsgrund" type="backendEnum" typeData={{ ident: "cancellationReasonService" }} /> }
      </Row>
      { customerId && <>
        <Row><h3>Kunde löschen</h3></Row>
        <div>
          <Space size="small">
            <ConfirmPasswordButton type="default" danger onClick={onDelete} confirmTitle={`Kunde ${customer?.firstName} ${customer?.lastName} löschen?`} confirmContent={'Bitte geben Sie das Passwort ein, um den Kunden zu löschen:'}>
              Kunde löschen
            </ConfirmPasswordButton>
          </Space>
        </div>
      </>}
    </AdvancedForm>
  )
}

export default CustomerMainForm;
